import React, { useState } from 'react'
import { CircularProgress, SelectChangeEvent } from '@mui/material'
import StyledButton from '../../../components/Button/Button'
import FormInput from '../../../components/Common/FormInput'
import FormSelect from '../../../components/Common/FormSelect'
import useSendBroadcast from '../services/hooks/usePostSendBroadcast'
import useValidation from '../../../hooks/useValidation'
import { TermsType } from '../../../utils/config/auth-enums'
import Popup from '@/components/Common/Popup'

interface FromDataType {
  userRole: TermsType
  title: string
  textUrl: string
}

const BroadcastNotifications: React.FC = () => {
  const inputValidationRules = {
    userRole: [{ required: true, message: 'Please select user role.' }],
    title: [{ required: true, message: 'Please enter your title.' }],
    textUrl: [{ required: true, message: 'Please enter your url.' }],
  }

  const [, setErrorMsg] = useState<string>('')
  const [showPopup, setShowPopup] = useState<boolean>(false)
  const [formData, setFormData] = useState<FromDataType>({
    userRole: TermsType.PARTICIPANT,
    title: '',
    textUrl: '',
  })
  const { errors, validateInputs } = useValidation(
    {
      userRole: formData.userRole,
      title: formData.title,
      textUrl: formData.textUrl,
    },
    inputValidationRules,
  )

  const { mutate: sendBroadcast, isPending } = useSendBroadcast()

  const options = [
    { value: TermsType.PARTICIPANT, label: 'Participant' },
    { value: TermsType.SUPERVISOR, label: 'Supervisor' },
  ]

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }))
  }

  const handleRoleChange = (event: SelectChangeEvent<unknown>) => {
    const { value } = event.target
    setFormData((prevData) => ({
      ...prevData,
      userRole: value as TermsType,
    }))
  }

  const handleClickDone = () => {
    setShowPopup(false)
  }

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!validateInputs()) {
      return
    }
    const requestData = {
      userType: formData.userRole,
      title: formData.title,
      text: formData.textUrl,
    }
    sendBroadcast(requestData, {
      onSuccess: () => {
        setShowPopup(true)
        setFormData({
          userRole: TermsType.PARTICIPANT,
          title: '',
          textUrl: '',
        })
      },
      onError: (error) => {
        setErrorMsg(error.message || 'Something went wrong. Please try again.')
      },
    })
  }

  return (
    <>
      <form onSubmit={handleSubmit}>
        <div className="mt-6 flex flex-col gap-4 w-full max-w-[670px]">
          <div className="flex flex-col gap-4">
            <FormSelect
              value={formData.userRole}
              onChange={handleRoleChange}
              label="Select User Type"
              name="userRole"
              options={options}
              error={!!errors.userRole}
              helperText={errors.userRole}
              sx={{ minWidth: '120px' }}
            />
            <FormInput
              type="text"
              label="Title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              error={!!errors.title}
              helperText={errors.title}
            />
            <FormInput
              type="text"
              label="Text / URL"
              name="textUrl"
              value={formData.textUrl}
              onChange={handleInputChange}
              error={!!errors.textUrl}
              helperText={errors.textUrl}
            />
          </div>

          <StyledButton
            className="h-14 w-28"
            type="submit"
            disabled={
              ![TermsType.PARTICIPANT, TermsType.SUPERVISOR].includes(
                formData.userRole,
              ) ||
              formData.title === '' ||
              formData.textUrl === '' ||
              isPending
            }
          >
            {isPending ? (
              <CircularProgress className="!text-[#181a21] !text-2xl" />
            ) : (
              'Send'
            )}
          </StyledButton>
        </div>
      </form>

      {showPopup && (
        <Popup
          title="Notification sent Successfully"
          message="Your notification has been successfully sent! Thank you for keeping everyone informed."
          buttonText="Done"
          onButtonClick={handleClickDone}
        />
      )}
    </>
  )
}

export default BroadcastNotifications
